import React, { useEffect, useState, useRef, useContext } from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  Raycaster,
  // AxesHelper
} from 'three';

import { animate } from 'framer-motion';

import ChartIntroLabel from '../ChartIntroLabel/ChartIntroLabel';
import RawHtml from '../RawHtml';

import {
  createBaseScene,
  alignHtmlElPositionWithWorldSpacePoint,
} from '../../threejs/utils/sceneHelpers';

// import {
//   createRenderPipeline,
//   createRenderPipelineWithOptionalBloom,
// } from '../../threejs/utils/createRenderPipeline';

import { createPieBars, scaleBarX } from '../../threejs/utils/barHelpers';

import { throttle } from 'throttle-debounce';

import useMultiplexedThreeJs from '../../hooks/useMultiplexedThreeJs';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import useWindowWidth from '../../hooks/useWindowWidth';

import { AnimationsActiveContext } from '../../containers/BasePage';

import {
  GRADIENT_ORANGE_PINK,
  GRADIENT_BLUE_PURPLE,
  GRADIENT_PINK_PURPLE,
  GRADIENT_GRAY,
} from '../../threejs/constants';

// const PIE_BARS_TOTAL_WIDTH = 95;
const PIE_BARS_TOTAL_WIDTH = 85;
const PIE_BAR_RIGHT_MARGIN = 0.2;

import s from './PieBarChart3d.module.scss';

const getElementIntersections = (elements) => {
  const intersectionMap = {};

  elements.forEach((element, index) => {
    if (!element) {
      return;
    }

    const rect1 = element.getBoundingClientRect();
    intersectionMap[index] = [];

    elements.forEach((otherElement, otherIndex) => {
      if (index === otherIndex) {
        return;
      }
      const rect2 = otherElement.getBoundingClientRect();
      if (
        rect1.bottom >= rect2.top &&
        rect1.top <= rect2.bottom &&
        rect1.right >= rect2.left &&
        rect1.left <= rect2.right
      ) {
        intersectionMap[index].push(otherIndex);
      }
    });
  });

  let fixedIndexes = [];
  let intersectedIndexesForTranslation = [];

  for (const [elIndex, intersections] of Object.entries(intersectionMap)) {
    if (fixedIndexes.indexOf(parseInt(elIndex)) !== -1) {
      continue;
    }

    intersections.forEach((intersectIndex) => {
      intersectedIndexesForTranslation.push(intersectIndex);

      fixedIndexes.push(parseInt(elIndex));
      fixedIndexes.push(parseInt(intersectIndex));
    });
  }

  return intersectedIndexesForTranslation;
};

const PieBarChart3d = (props) => {
  const {
    title,
    description,
    infoTitle,
    infoText,
    infoLink,
    textTheme,
    items,
  } = props;

  const { animationsActive } = useContext(AnimationsActiveContext) || {};

  const windowWidth = useWindowWidth(120);
  const [revealTransitionComplete, setRevealTransitionComplete] =
    useState(false);

  const labelRefs = useRef([]);
  const labelIntersectionRefs = useRef([]);

  const [inViewRef, isVisible] = useIntersectionObserver(0.5, undefined, true);
  const { onThreeJsCanvasReady, handleCanvasRef, canvas } =
    useMultiplexedThreeJs({ debug: false });

  const [
    intersectedLabelRefIndexesForTranslate,
    setIntersectedLabelRefIndexesForTranslate,
  ] = useState([]);

  useEffect(() => {
    setIntersectedLabelRefIndexesForTranslate([]);
    setTimeout(
      () =>
        setIntersectedLabelRefIndexesForTranslate(
          getElementIntersections(labelIntersectionRefs.current)
        ),
      1300
    );
  }, [windowWidth, isVisible]);

  onThreeJsCanvasReady(
    ({ renderer, renderMultiplexedThreeJsScene }) => {
      if (!items) {
        return;
      }

      const aspect = canvas.offsetWidth / canvas.offsetHeight;
      const { camera, scene } = createBaseScene({
        aspect,
        cameraPosition: {
          x: 0,
          y: 30,
          z: 33,
        },
        lightPosition: {
          x: -10,
          y: 10,
          z: 30,
        },
        lightIntensity: 10,
      });

      // const { renderWithPipeline, updatePipeline } =
      //   createRenderPipelineWithOptionalBloom(canvas, renderer, scene, camera, {
      //     bloomThreshold: 0.155,
      //     bloomStrength: 0.28, //0.28,
      //     bloomRadius: 5, //8,
      //   });

      // const { renderWithPipeline, updatePipeline } =
      //   createRenderPipeline(canvas, renderer, scene, camera);

      // const axesHelper = new AxesHelper(15);
      // scene.add(axesHelper);

      // init bars

      let { bars, barMaterials } = createPieBars({
        items,
        totalWidth: PIE_BARS_TOTAL_WIDTH,
        marginRight: PIE_BAR_RIGHT_MARGIN,
      });

      bars.forEach((b) => {
        scene.add(b.mesh);
      });

      const initialBarScale = !revealTransitionComplete ? 0.001 : 1;
      bars.forEach((b) => {
        scaleBarX(b, initialBarScale, PIE_BARS_TOTAL_WIDTH);
        b.mesh.rotation.x = Math.PI * -0.35;
      });

      // get mouse position relative to canvas

      let mousePointer = {
        rawX: -1,
        rawY: -1,
        x: -1,
        y: -1,
        ndcX: -1,
        ndcY: -1,
        directionX: 0,
      };

      let prevMousePointerPosition = {
        ...mousePointer,
      };

      const onPointerMove = throttle(25, (e) => {
        const bounds = canvas.getBoundingClientRect();

        const rawX = e.clientX - bounds.left;
        const rawY = e.clientY - bounds.top;

        const x = rawX / bounds.width;
        const y = 1 - rawY / bounds.height;

        const newPosition = {
          // relative canvas coords in pixels in range [0, max canvas width/height in pixels]
          rawX,
          rawY,
          // normalized relative canvas coords in range [0, 1]
          x,
          y,
          // normalized relative canvas coords in range [-1, 1]
          ndcX: x * 2 - 1,
          ndcY: (y - 1) * 2 + 1,
          // horizontal movement direction
          directionX: Math.sign(rawX - prevMousePointerPosition.rawX),
          directionY: Math.sign(rawY - prevMousePointerPosition.rawY),
        };

        mousePointer = newPosition;
        prevMousePointerPosition = {
          ...mousePointer,
        };
      });

      window.addEventListener('pointermove', onPointerMove);

      const raycaster = new Raycaster();
      let prevBarIntersects = [];

      const getBarsIntersectedByPointer = () => {
        raycaster.setFromCamera(
          {
            x: mousePointer.ndcX,
            y: mousePointer.ndcY,
          },
          camera
        );

        const intersects = raycaster.intersectObjects(scene.children, true);
        const barIntersects =
          intersects.filter((o) => o.object.name.indexOf('bar-') > -1) || [];

        // used for pointer/mouse out detection
        const origBarIntersects = [...barIntersects];
        const currentBarIntersects = barIntersects.filter(
          (hb) =>
            prevBarIntersects.filter((p) => p.object.uuid === hb.object.uuid)
              .length === 0
        );

        prevBarIntersects = [...origBarIntersects];

        return currentBarIntersects;
      };

      // trigger reveal animation for bars

      if (isVisible && !revealTransitionComplete && animationsActive) {
        bars.forEach((bar) => {
          const animDuration = 1;

          animate(0, 1, {
            duration: animDuration,
            onUpdate: (p) => {
              scaleBarX(bar, p, PIE_BARS_TOTAL_WIDTH);
            },
            onComplete: () => {
              setRevealTransitionComplete(true);
            },
          });

          animate(bar.mesh.rotation.x, bar.mesh.rotation.x + Math.PI * 2, {
            duration: animDuration,
            onUpdate: (a) => {
              bar.mesh.rotation.x = a;
            },
          });
        });
      } else if (isVisible && !revealTransitionComplete && !animationsActive) {
        setRevealTransitionComplete(true);
      }

      // main render loop
      renderMultiplexedThreeJsScene(
        (time, resolution, renderer, scissorOffsets) => {
          // update stuff
          camera.aspect = resolution.width / resolution.height;
          camera.updateProjectionMatrix();

          // updatePipeline(resolution, time);

          Object.values(barMaterials).forEach(({ customGlassUniforms }) => {
            // NOTE!!!!!!
            // If using a bloom pipeline, this should be set to:
            // customGlassUniforms.uScissorOffsets.value = [0, 0];
            customGlassUniforms.uScissorOffsets.value = scissorOffsets;

            customGlassUniforms.uResolution.value = [
              resolution.width,
              resolution.height,
            ];
            customGlassUniforms.uMousePointer.value = [
              mousePointer.x,
              mousePointer.y,
            ];

            customGlassUniforms.uTime.value = time;
            customGlassUniforms.uPixelRatio.value = renderer.getPixelRatio();
          });

          // rotate bars on pointer hover
          getBarsIntersectedByPointer().forEach((bar) => {
            const mouseXDiff = Math.abs(
              bar.object.userData?.prevMousePointer?.rawX - mousePointer.rawX
            );
            const mouseYDiff = Math.abs(
              bar.object.userData?.prevMousePointer?.rawY - mousePointer.rawY
            );

            // bail if mouse hasn't moved
            // this prevents endless rotation triggered when hovering bar edges etc.
            if (mouseXDiff <= 1 && mouseYDiff <= 1) {
              return;
            }

            animate(Math.PI * -mousePointer.directionY, Math.PI * -0.35, {
              // duration: 1,
              type: 'spring',
              stiffness: 150,
              velocity: 8,
              mass: 0.7,
              onPlay: () => {
                bar.object.userData.prevMousePointer = { ...mousePointer };
              },
              onUpdate: (r) => {
                bar.object.parent.rotation.x = r;
              },
            });
          });

          // finally, render all the stuff
          // renderWithPipeline();
          renderer.render(scene, camera);

          // ...and handle alignment of html labels
          labelRefs.current.forEach((label, i) => {
            const bar = bars[i];

            const worldSpacePosition =
              bar?.labelAlignment === 'right'
                ? bar.rightPosition
                : bar.leftPosition;

            alignHtmlElPositionWithWorldSpacePoint({
              camera,
              resolution,
              worldSpacePosition,
              element: label.element,
              centerAlign: false,
              leftAlign: bar?.labelAlignment === 'right',
            });
          });
        }
      );

      return () => {
        window.removeEventListener('pointermove', onPointerMove);
      };
    },
    [labelRefs, isVisible, revealTransitionComplete]
  );

  const rootCls = classNames(s.Root, {
    [s.Visible]: isVisible,
    [s.TextColorDark]: textTheme === 'dark',
  });

  return (
    <div ref={inViewRef} className={rootCls}>
      <div className="grid">
        <div className={s.TitleContainer}>
          {title && (
            <ChartIntroLabel
              title={title}
              description={description}
              infoTitle={infoTitle}
              infoText={infoText}
              infoLink={infoLink}
              theme={textTheme}
            />
          )}
        </div>
      </div>
      <div className="grid">
        <div className={s.Container}>
          <div ref={handleCanvasRef} className={s.Canvas}>
            <div className={s.Labels}>
              {items.map((v, i) => {
                const innerLabelWrapCls = classNames(s.InnerLabelWrap, {
                  [s.Visible]: isVisible || revealTransitionComplete,
                  [s.TopPad]:
                    intersectedLabelRefIndexesForTranslate.indexOf(
                      parseInt(i)
                    ) !== -1,
                  [s.RightAligned]: v?.labelAlignment === 'right',
                });

                return (
                  <div key={i}>
                    <div
                      className={s.LabelWrap}
                      ref={(el) => {
                        labelRefs.current[i] = {
                          element: el,
                        };
                      }}>
                      <div
                        ref={(el) => {
                          labelIntersectionRefs.current[i] = el;
                        }}
                        className={innerLabelWrapCls}>
                        <span
                          className={s.Label}
                          id={`pie-chart-3d-label-${i}`}>
                          <RawHtml html={v.label} />
                        </span>
                        <span
                          className="sr-only"
                          aria-labelledby={`pie-chart-3d-label-${i}`}>
                          {v.value}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PieBarChart3d.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  infoTitle: PropTypes.string,
  infoText: PropTypes.string,
  infoLink: PropTypes.object,
  textTheme: PropTypes.oneOf(['light', 'dark']),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      barColor: PropTypes.oneOf([
        '',
        GRADIENT_ORANGE_PINK,
        GRADIENT_BLUE_PURPLE,
        GRADIENT_PINK_PURPLE,
        GRADIENT_GRAY,
      ]),
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
      labelColor: PropTypes.string,
      labelAlignment: PropTypes.oneOf(['', 'left', 'right']),
    }).isRequired
  ).isRequired,
};

PieBarChart3d.defaultProps = {
  items: [],
};

export default PieBarChart3d;
